import { build as viteBuild } from 'vite';
import { createViteConfig } from '../config/ViteConfig.js';
import { BaseAdapter, BuildContext, DeploymentTarget } from '../adapters/BaseAdapter.js';
import { StaticAdapter } from '../adapters/StaticAdapter.js';
import { BodifyAdapter } from '../adapters/BodifyAdapter.js';
import fs from 'fs-extra';
import * as path from 'path';

export interface BuildOptions {
	mode?: 'development' | 'production';
	target?: string;
	adapter?: BaseAdapter;
	watch?: boolean;
}

export class BuildManager {
	private cwd: string;
	private adapters: Map<string, BaseAdapter> = new Map();

	constructor(cwd: string) {
		this.cwd = cwd;
		this.registerDefaultAdapters();
	}

	/**
	 * Register default adapters
	 */
	private registerDefaultAdapters(): void {
		this.registerAdapter('static', new StaticAdapter());
		this.registerAdapter('bodify', new BodifyAdapter());
	}

	/**
	 * Register a custom adapter
	 */
	registerAdapter(name: string, adapter: BaseAdapter): void {
		this.adapters.set(name, adapter);
	}

	/**
	 * Get an adapter by name
	 */
	getAdapter(name: string): BaseAdapter | undefined {
		return this.adapters.get(name);
	}

	/**
	 * List all available adapters
	 */
	listAdapters(): string[] {
		return Array.from(this.adapters.keys());
	}

	/**
	 * Build the project
	 */
	async build(options: BuildOptions = {}): Promise<void> {
		const {
			mode = 'production',
			target = 'static',
			adapter: customAdapter,
			watch = false,
		} = options;

		console.log(`[Frame Build] Starting ${mode} build for ${target}...`);

		// Get the adapter
		const adapter = customAdapter || this.getAdapter(target);
		if (!adapter) {
			throw new Error(`Unknown build target: ${target}. Available targets: ${this.listAdapters().join(', ')}`);
		}

		// Create build context
		const buildContext: BuildContext = {
			cwd: this.cwd,
			outDir: adapter.getOptions().outDir || 'dist',
			mode,
			target: {
				name: target,
				platform: adapter.getPlatform(),
				config: {},
			},
		};

		try {
			// Prepare adapter-specific build configuration
			const adapterConfig = await adapter.prepareBuild(buildContext);

			// Create Vite configuration
			const viteConfig = await createViteConfig(this.cwd);

			// Merge adapter configuration
			const finalConfig = this.mergeConfig(viteConfig, adapterConfig, { mode });

			console.log('[Frame Build] Building with Vite...');

			// Build with Vite
			await viteBuild(finalConfig);

			// Process build output with adapter
			await adapter.processBuild(buildContext);

			// Generate deployment files
			await adapter.generateDeploymentFiles(buildContext);

			// Validate deployment
			const isValid = await adapter.validateDeployment(buildContext);
			if (!isValid) {
				throw new Error('Deployment validation failed');
			}

			console.log(`[Frame Build] Build completed successfully for ${target}`);
			console.log(`[Frame Build] Output directory: ${buildContext.outDir}`);

		} catch (error) {
			console.error('[Frame Build] Build failed:', error);
			throw error;
		}
	}

	/**
	 * Preview the built application
	 */
	async preview(options: { port?: number; host?: string } = {}): Promise<void> {
		const { preview } = await import('vite');
		const viteConfig = await createViteConfig(this.cwd);

		// Ensure the dist directory exists
		const distPath = path.join(this.cwd, viteConfig.build?.outDir || 'dist');
		if (!await fs.pathExists(distPath)) {
			throw new Error(`Build output directory not found: ${distPath}. Please run 'frame build' first.`);
		}

		const previewConfig = {
			...viteConfig,
			// Explicitly set the root to the project directory
			root: this.cwd,
			// Explicitly set the build output directory
			build: {
				...viteConfig.build,
				outDir: viteConfig.build?.outDir || 'dist',
			},
			preview: {
				port: options.port || 4173,
				host: options.host || 'localhost',
				open: false, // Don't auto-open in terminal environments
			},
		};

		console.log('[Frame Build] Starting preview server...');
		console.log(`[Frame Build] Serving from: ${distPath}`);
		const server = await preview(previewConfig);
		server.printUrls();
	}

	/**
	 * Fallback preview server using simple static file serving
	 */
	async previewFallback(options: { port?: number; host?: string } = {}): Promise<void> {
		const viteConfig = await createViteConfig(this.cwd);
		const distPath = path.join(this.cwd, viteConfig.build?.outDir || 'dist');

		if (!await fs.pathExists(distPath)) {
			throw new Error(`Build output directory not found: ${distPath}. Please run 'frame build' first.`);
		}

		// Use Node.js built-in HTTP server for serving static files
		const http = await import('http');
		const url = await import('url');
		const { readFileSync, existsSync, statSync } = await import('fs');

		const port = options.port || 4173;
		const host = options.host || 'localhost';

		const server = http.createServer((req, res) => {
			const parsedUrl = url.parse(req.url || '');
			let pathname = parsedUrl.pathname || '/';

			// Handle SPA routing - serve index.html for non-file requests
			if (pathname === '/' || (!pathname.includes('.') && !pathname.endsWith('/'))) {
				pathname = '/index.html';
			}

			const filePath = path.join(distPath, pathname);

			// Security check - ensure file is within dist directory
			if (!filePath.startsWith(distPath)) {
				res.writeHead(403);
				res.end('Forbidden');
				return;
			}

			try {
				if (existsSync(filePath) && statSync(filePath).isFile()) {
					const ext = path.extname(filePath).toLowerCase();
					const contentType = this.getContentType(ext);

					res.writeHead(200, { 'Content-Type': contentType });
					res.end(readFileSync(filePath));
				} else {
					// Serve index.html for SPA fallback
					const indexPath = path.join(distPath, 'index.html');
					if (existsSync(indexPath)) {
						res.writeHead(200, { 'Content-Type': 'text/html' });
						res.end(readFileSync(indexPath));
					} else {
						res.writeHead(404);
						res.end('Not Found');
					}
				}
			} catch (error) {
				res.writeHead(500);
				res.end('Internal Server Error');
			}
		});

		server.listen(port, host, () => {
			console.log(`[Frame Build] Static preview server running on:`);
			console.log(`  ➜  Local:   http://${host}:${port}/`);
			console.log(`  ➜  Serving: ${distPath}`);
		});
	}

	private getContentType(ext: string): string {
		const types: Record<string, string> = {
			'.html': 'text/html',
			'.css': 'text/css',
			'.js': 'application/javascript',
			'.mjs': 'application/javascript',
			'.json': 'application/json',
			'.png': 'image/png',
			'.jpg': 'image/jpeg',
			'.jpeg': 'image/jpeg',
			'.gif': 'image/gif',
			'.svg': 'image/svg+xml',
			'.ico': 'image/x-icon',
			'.woff': 'font/woff',
			'.woff2': 'font/woff2',
			'.ttf': 'font/ttf',
			'.otf': 'font/otf',
		};
		return types[ext] || 'application/octet-stream';
	}

	/**
	 * Merge configurations with proper precedence
	 */
	private mergeConfig(baseConfig: any, adapterConfig: any, overrides: any): any {
		return {
			...baseConfig,
			...overrides,
			build: {
				...baseConfig.build,
				...adapterConfig.build,
				...overrides.build,
			},
			server: {
				...baseConfig.server,
				...adapterConfig.server,
				...overrides.server,
			},
		};
	}

	/**
	 * Get build statistics
	 */
	async getBuildStats(outDir: string): Promise<BuildStats> {
		const distPath = path.join(this.cwd, outDir);

		if (!await fs.pathExists(distPath)) {
			throw new Error(`Build output directory not found: ${distPath}`);
		}

		const files = await this.getFileList(distPath);
		const totalSize = files.reduce((sum, file) => sum + file.size, 0);

		return {
			totalSize,
			fileCount: files.length,
			files: files.sort((a, b) => b.size - a.size),
			outputDir: distPath,
		};
	}

	private async getFileList(dirPath: string, relativeTo = dirPath): Promise<FileInfo[]> {
		const files: FileInfo[] = [];
		const entries = await fs.readdir(dirPath, { withFileTypes: true });

		for (const entry of entries) {
			const fullPath = path.join(dirPath, entry.name);
			const relativePath = path.relative(relativeTo, fullPath);

			if (entry.isDirectory()) {
				const subFiles = await this.getFileList(fullPath, relativeTo);
				files.push(...subFiles);
			} else {
				const stats = await fs.stat(fullPath);
				files.push({
					name: entry.name,
					path: relativePath,
					size: stats.size,
					type: this.getFileType(entry.name),
				});
			}
		}

		return files;
	}

	private getFileType(filename: string): string {
		const ext = filename.split('.').pop()?.toLowerCase();

		if (['js', 'mjs'].includes(ext!)) return 'javascript';
		if (['css'].includes(ext!)) return 'stylesheet';
		if (['html'].includes(ext!)) return 'html';
		if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext!)) return 'image';
		if (['woff', 'woff2', 'ttf', 'otf'].includes(ext!)) return 'font';

		return 'other';
	}
}

interface BuildStats {
	totalSize: number;
	fileCount: number;
	files: FileInfo[];
	outputDir: string;
}

interface FileInfo {
	name: string;
	path: string;
	size: number;
	type: string;
} 